import { CheckCircleIcon } from "@phosphor-icons/react"

import { Demand2TotalColumn } from "~/components/demand-2/demand/totalColumnIds"
import { getColumnNaming } from "~/components/planning/ProductTable/utils/columnUtils"
import { useFilters } from "~/hooks/useFilters"
import type { IVariantSummary } from "~/hooks/useVariantTableData"
import type { FilterFieldType } from "~/utils/filter"

import { PENDING_LABEL, REVIEWED_LABEL, type VariantReview } from "./reviewStatus"
import { useVariantReviews } from "./useVariantReviews"

/**
 * Page-filter field for Reviewed/Pending. Not a column of the variant read (reviews live in
 * `DemandVariantReview`), so tabs stamp it on before their page filters run.
 */
export const REVIEW_FIELD = "demandReview"

/** Module-level, so the filter bar's field list stays stable. */
export const REVIEW_FILTER_FIELD: FilterFieldType = {
  value: REVIEW_FIELD,
  name: getColumnNaming(Demand2TotalColumn.Review)?.columnTitle ?? "Review",
  type: "enum",
  icon: <CheckCircleIcon />,
  options: [REVIEWED_LABEL, PENDING_LABEL],
}

/**
 * The reviews to stamp, or null while no review filter is set: a toggle moves no number, so it
 * should not hand the grids fresh rows unless a filter reads it.
 */
export function useReviewsForFilter(): ReadonlyMap<string, VariantReview> | null {
  const { reviews } = useVariantReviews()
  const { filters } = useFilters()
  return filters.some((f) => f.field === REVIEW_FIELD) ? reviews : null
}

/** Passes `variants` through untouched when `reviews` is null (no review filter set). */
export const stampReviews = <V extends IVariantSummary>(
  variants: V[],
  reviews: ReadonlyMap<string, VariantReview> | null,
): V[] =>
  reviews
    ? variants.map((v) => ({
        ...v,
        [REVIEW_FIELD]: reviews.has(v.variantId) ? REVIEWED_LABEL : PENDING_LABEL,
      }))
    : variants
